import * as React from "react"
import { Alert, Col, Empty, Row, Spin } from "antd"
import { resolve } from 'src/inversify.config'
import autobind from "autobind-decorator"
import { withTranslation, WithTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import ContentTitle from 'src/core/ui/content-header'
import HttpService, { formatMessage } from "../../../core/services/http.service"
import Timesheet, { GetProjectTaskNames } from 'src/components/timesheet'
import { TimesheetTaskItem } from 'src/pages/timesheet'
import { unixEpochToDate } from "../../../core/utils/object"
import moment from "moment"
import { withIdentity } from "../../../core/services/authentication"
import { connect } from "redux-scaffolding-ts"
import { ProjectSummary, TimesheetProjectsStore } from "../../../stores/projects-store"
import { Query } from "../../../core/stores/dataStore"
import { UserProfileProps, withUserProfile } from "../../../components/user-profile"

interface TimesheetViewProps extends RouteComponentProps<{ companyId: string, employeeId: string, timesheetId: string }>, UserProfileProps {
  onClose: (id: string | undefined, item?: TimesheetItem) => void
}

interface TimesheetViewState {
  isBusy: boolean
  error?: string
  timesheet?: TimesheetItem
  projectIdToSummary: Map<string, ProjectSummary>
  allProjects: string[]
}

export type TimesheetStatus = 'Draft' | 'Pending' | 'Approved' | 'Rejected'

interface TimesheetSummary {
  id: string,
  employeeId: string,
  employeeTitle: string,
  fromDate: Date,
  toDate: Date,
  status: TimesheetStatus,
  totalHours: number
  minHours: number
  maxHours: number
}

interface TimesheetTaskEntryItem {
  day: number,
  planned: number,
  hours: number,
  notes: string
}

interface TimesheetItem extends TimesheetSummary {
  companyTitle: string
  tasks: TimesheetTaskItem[]
}

@connect(["projects", TimesheetProjectsStore])
class TimesheetView extends React.Component<TimesheetViewProps & WithTranslation, TimesheetViewState> {
  @resolve(HttpService)
  private httpService!: HttpService

  private get projectsStore() {
    return (this.props as any).projects as TimesheetProjectsStore
  }

  constructor(props: TimesheetViewProps & WithTranslation) {
    super(props)
    this.state = {
      allProjects: [],
      isBusy: false,
      projectIdToSummary: new Map(),
    }
  }

  componentWillMount(): void {
    this.load()
  }

  componentWillUpdate() {
    if (!this.checkAuth())
      this.props.history.push('/')
  }

  @autobind
  private checkAuth() {
    return this.props.userProfile && this.props.userProfile.roles &&
      (this.props.userProfile.roles.includes("Manager")
        || this.props.userProfile.roles.includes("Administrator")
        || this.props.userProfile.roles.includes("ClientAdmin")
        || this.props.userProfile.roles.includes("FIConsultant")
        || this.props.userProfile.roles.includes("CompanyDataViewer"))
  }

  @autobind
  private isTaxInspector() {
    return this.props.userProfile && this.props.userProfile.roles &&
      (this.props.userProfile.roles.includes("CompanyDataViewer")
        && !(this.props.userProfile.roles.includes("Manager")
          || this.props.userProfile.roles.includes("Administrator")
          || this.props.userProfile.roles.includes("ClientAdmin")
          || this.props.userProfile.roles.includes("FIConsultant")))
  }

  @autobind
  private replaceHistoryWithTimesheet(timesheet: TimesheetItem) {
    let { t } = this.props
    this.props.history.replace(`/time-tracking/admin/companies/${this.props.match.params.companyId}/employees/${this.props.match.params.employeeId}/timesheets/${timesheet.id}`,
      {
        titles: [...(this.props.location.state ? (this.props.location.state as any).titles || [] : []),
        { url: `/time-tracking/admin/companies/${this.props.match.params.companyId}`, title: timesheet.companyTitle },
        { url: `/time-tracking/admin/companies/${this.props.match.params.companyId}/employees/${this.props.match.params.employeeId}`, title: timesheet.employeeTitle },
        { url: `/time-tracking/admin/companies/${this.props.match.params.companyId}/employees/${this.props.match.params.employeeId}/timesheets/`, title: t('timesheets') }]
      })
  }

  private distinct(value: any, index: any, self: any) {
    return self.indexOf(value) === index
  }

  @autobind
  private load() {
    var self = this
    this.setState({ isBusy: true })

    this.httpService.get<any>(`${process.env.PUBLIC_URL}/api/v1/timesheet/${this.props.match.params.companyId}/${this.props.match.params.employeeId}/${this.props.match.params.timesheetId}`).then(async (result: any) => {
      let timesheet = ({
        ...result.data,
        fromDate: unixEpochToDate(result.data.fromDate),
        toDate: moment(unixEpochToDate(result.data.fromDate)).add('days', 6).toDate(),
      } as TimesheetItem)

      timesheet.tasks.sort((a, b) => GetProjectTaskNames(a)[0].localeCompare(GetProjectTaskNames(b)[0]))

      let projectsQuery: Query = {
        searchQuery: '',
        orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }],
        parameters: { projectNames: timesheet.tasks.map(task => task.title.split(" - ")[0]).filter(this.distinct) },
        skip: 0,
        take: 1000
      }

      self.replaceHistoryWithTimesheet(timesheet)
      self.projectsStore.changeUrlByCompany(self.props.match.params.companyId)
      await self.projectsStore.getAllAsync(projectsQuery, undefined).then(result => {
        let projectIdToSummary = new Map<string, ProjectSummary>()
        let projects = result.items as ProjectSummary[]
        projects.forEach(result => {
          projectIdToSummary.set(result.id, result)
        })

        self.setState({
          isBusy: false,
          error: undefined,
          timesheet: timesheet,
          projectIdToSummary: projectIdToSummary
        })
      }).catch(error => {
        this.setState({ error: formatMessage(error), isBusy: false })
      })
    }).catch((error: any) => {
      this.setState({ error: formatMessage(error), isBusy: false })
    })
  }

  public render() {
    var self = this
    const { t } = this.props
    const isFutureAllowed = this.props.userProfile.companies.firstOrDefault(x => x.companyId == this.props.match.params.companyId)?.isFutureAllowed || false;
    return <div>
      {this.state.timesheet && <Row gutter={0}>
        <Col span={16}>
          <ContentTitle type={t("Timesheet")} title={this.state.timesheet.employeeTitle} />
        </Col>
        <Col span={8}>
        </Col>
      </Row>}
      <Row type={'flex'} align='top' justify='space-between'>
        {this.state.error && (
          <Alert
            type="error"
            message={t("An error has occurred")}
            description={formatMessage(this.state.error)}
          />
        )}
        <div style={{ width: '100%' }}>
          <Spin size='large' spinning={this.state.isBusy}>
            {
              this.state.timesheet && <Timesheet
                isFutureAllowed={isFutureAllowed}
                employeeId={this.state.timesheet.employeeId}
                comapnyId={this.props.match.params.companyId}
                timesheetId={this.state.timesheet.id}
                start={this.state.timesheet.fromDate}
                end={this.state.timesheet.toDate}
                minHours={this.state.timesheet.minHours}
                maxHours={this.state.timesheet.maxHours}
                readonly={true}
                canManage={false}
                status={this.state.timesheet.status}
                rowsDataSource={{
                  title: t('Tasks'),
                  render: (item: any) => <span>{GetProjectTaskNames(item.object)[1]}</span>,
                  onSearch: (item: any, q: any) => (item.object.title && q) ? item.object.title.toUpperCase().includes(q.toUpperCase()) : false,
                  items: (this.state.timesheet.tasks || []).map(o => ({
                    rowKey: o.projectTaskId,
                    object: o,
                    projectTitle: GetProjectTaskNames(o as any)[0],
                    projectTaskTitle: GetProjectTaskNames(o as any)[1],
                    projectId: o.projectId,
                    isFavorite: o.isFavorite,
                    disabled: o.disabledForEmployee
                  }))
                }}
                cellsDataSource={{
                  items: (this.state.timesheet.tasks || []).flatMap(task => !task.entries ? [] : task.entries.map(cell => ({ rowKey: task.projectTaskId, colKey: cell.day, referenceKey: task.projectId, value: cell.hours, optionalValue: cell.notes, object: cell }))),
                  defaultValue: 0
                }}
                projectIdToSummary={this.state.projectIdToSummary}
                showFavorite={false} />}
            {!this.state.timesheet && <Empty />}
          </Spin>
        </div>
      </Row>
    </div>
  }
}

// Wire up the React component to the Redux store
export default withUserProfile(withIdentity(withRouter(withTranslation()(TimesheetView) as any))) as any as React.ComponentClass<TimesheetViewProps>